// 2. api.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { phpClient } from "../../../network";
import {
    AddNotesApiResponse,
    AddNotesPayload,
    AddTimecardApiResponse,
    AddTimecardPayload,
    AdjustTimeCardPayload,
    AdjustTimeCardResponse,
    GetAuditApiResponse,
    GetAuditPayload,
    GetNotesApiResponse,
    GetNotesPayload,
    GetTimeCardPayload,
    GetTimeCardSummaryPayload,
    GroupedTimeCardApiResponse,
    GroupedTimeCardPayload,
    TimeCardApiResponse,
    TimecardRecordDayDetails,
    TimeCardSummaryApiResponse,
} from "../../../types/state/timecards";
import {
    AddTimecardRequestPayload,
    GetAllTimesheetRecordsPayload,
    GetTimecardRequestsPayload,
} from "../../../types/request/timecard";
import {
    AddTimecardRequestResponse,
    GetAllTimesheetRecordsResponse,
    GetTimecardRequestsResponse,
} from "../../../types/response/timecard";
import { getStartAndEndOfWeek, getStartAndEndOfWeekByStartDay, getTodayAtMidnight, getTomorrowAtMidnight } from "../../../utils/employeeTimeCardhelper";
import { getCachedData, setCachedData } from '../../../utils/storageCache';

export const getEmployeeTimeCard = createAsyncThunk(
    "timecards/employeeTimecard",
    async (payload: GetTimeCardPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<TimeCardApiResponse>({
                url: "/timecard",
                data: payload,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getEmployeeTimeCardSummary = createAsyncThunk(
    "timecards/employeeTimecardSummary",
    async (payload: GetTimeCardSummaryPayload = {}, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<TimeCardSummaryApiResponse>({
                url: "/timecard/get_summary",
                data: payload,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Create Async Thunk for /timecard/grouped API call
export const getEmployeeTimeCardGrouped = createAsyncThunk(
    "timecards/employeeTimecardGrouped",
    async (payload: GroupedTimeCardPayload = {}, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<GroupedTimeCardApiResponse>({
                url: "/timecard/grouped",
                data: payload,
            });
            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getTodayTimeCardSummary = createAsyncThunk(
    "timecards/todayEmployeeTimecardSummary",
    async (employee_id: number, { rejectWithValue }) => {
        try {
            const payload: GetTimeCardSummaryPayload = {
                employee_id,
                start_date: getTodayAtMidnight(),
                end_date: getTomorrowAtMidnight(),
            };
            const response = await phpClient.post<TimeCardSummaryApiResponse>({
                url: "/timecard/get_summary",
                data: payload,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// This Week's Summary
export const getThisWeekTimeCardSummary = createAsyncThunk(
    "timecards/thisWeekEmployeeTimecardSummary",
    async (
        param: number | { employee_id: number; forceRefresh?: boolean; weekStartDay?: number | string },
        { rejectWithValue, getState }
    ) => {
        const employee_id = typeof param === 'number' ? param : param.employee_id;
        const forceRefresh = typeof param === 'number' ? false : (param.forceRefresh || false);
        const providedWeekStart = typeof param === 'number' ? undefined : param.weekStartDay;
        
        const cacheKey = `thisWeekSummaryCache_${employee_id}`;
        if (!forceRefresh) {
            const cached = getCachedData<any>(cacheKey);
            if (cached) {
                console.log("Cached data found for this week summary:", cached);
                return { summary: cached.data };
            }
        }
        try {
            console.log("Fetching PHP this week summary for employee:", employee_id);
            // Try to resolve week start day from state if not provided
            let weekStartDay: number | string | undefined = providedWeekStart;
            try {
                const state: any = getState();
                const user = state?.user?.user;
                // Prefer camelCase if present, else snake_case from raw payload
                console.log("User:", user);
                const fromCamel = user?.osEmpWorkweekStartDay;
                const fromSnake = user?.os_emp_workweek_start_day;
                weekStartDay = weekStartDay ?? fromCamel ?? fromSnake;
            } catch {}

            const { start, end } = getStartAndEndOfWeekByStartDay(weekStartDay);
            const payload: GetTimeCardSummaryPayload = {
                employee_id,
                start_date: start,
                end_date: end,
            };
            const response = await phpClient.post<TimeCardSummaryApiResponse>({
                url: "/timecard/get_summary",
                data: payload,
            });
            if (response.data?.summary) {
                setCachedData(cacheKey, response.data.summary);
            }
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch timecard summary");
        }
    }
);

export const getEmployeeTimeCardRecordDetail = createAsyncThunk(
    "timecards/getEmployeeTimecardRecordDetail",
    async (data: { 
        id: number; 
        employee_id?: number;
        extra_requested_fields?: string[];
    }, thunkAPI) => {
        const { rejectWithValue } = thunkAPI;
        try {
            const { id, employee_id, extra_requested_fields = ["notes_count", "audit_count"] } = data;
            const requestBody: Record<string, any> = {
                extra_requested_fields,
            };
            if (employee_id) {
                requestBody.employee_id = employee_id;
            }

            const response = await phpClient.post<{ data: TimecardRecordDayDetails }>({
                url: `/timecard/details/${id}`,
                data: requestBody,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getAuditLogsApi = createAsyncThunk<GetAuditApiResponse, GetAuditPayload>(
    "timecards/getAuditLogs",
    async (payload: GetAuditPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<GetAuditApiResponse>({
                url: "/timecard/get_audit",
                data: payload,
            });
            return response.data as GetAuditApiResponse; // Explicitly cast to the expected type
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const adjustTimeCardApi = createAsyncThunk<
    AdjustTimeCardResponse,
    AdjustTimeCardPayload,
    { rejectValue: string }
>("timecards/adjustTimeCard", async (payload: AdjustTimeCardPayload, { rejectWithValue }) => {
    try {
        const { record_id, ...restOfPayload } = payload;
        const response = await phpClient.post<AdjustTimeCardResponse>({
            url: `/timecard/adjust/${record_id}`,
            data: restOfPayload,
        });
        return response;
    } catch (error: any) {
        // Preserve backend validation text so timesheet window errors surface as-is in the toast.
        const message =
            error?.response?.data?.message || error?.message || "Error adjusting timecard";
        return rejectWithValue(message);
    }
});

// Redux Thunk for Get Notes API
export const getNotesToTimecardRecord = createAsyncThunk<GetNotesApiResponse, GetNotesPayload>(
    "timecards/getNotesToTimecardRecord",
    async (payload: GetNotesPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<GetNotesApiResponse>({
                url: "/timecard/get_notes",
                data: payload,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

// Redux Thunk for Add Notes API
export const addNotesToTimecardRecord = createAsyncThunk<AddNotesApiResponse, AddNotesPayload>(
    "timecards/addNotesToTimecardRecord",
    async (payload: AddNotesPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<AddNotesApiResponse>({
                url: "/timecard/add_notes",
                data: payload,
            });
            return response; // Explicitly cast to the expected type
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const addTimecardRecord = createAsyncThunk<AddTimecardApiResponse, AddTimecardPayload>(
    "timecards/addTimecardRecord",
    async (payload: AddTimecardPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<AddTimecardApiResponse>({
                url: "/timecard/add",
                data: payload,
            });
            return response;
        } catch (error: any) {
            console.log("error", error);
            // Preserve backend validation text so timesheet window errors surface as-is in the toast.
            const message =
                error?.response?.data?.message || error?.message || "An error occurred while adding timecard";
            return rejectWithValue(message);
        }
    }
);

export const addTimecardRequest = createAsyncThunk<AddTimecardRequestResponse, AddTimecardRequestPayload>(
    "timecards/addTimecardRequest",
    async (payload: AddTimecardRequestPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<AddTimecardRequestResponse>({
                url: "/timecard/add_timecard_request",
                data: payload,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred while adding timecard request");
        }
    }
);

export const getAllTimesheetRecords = createAsyncThunk<
    GetAllTimesheetRecordsResponse,
    GetAllTimesheetRecordsPayload,
    { rejectValue: unknown }
>(
    "timecards/getAllTimesheetRecords",
    async (payload: GetAllTimesheetRecordsPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<GetAllTimesheetRecordsResponse>({
                url: "/timecard/index",
                data: payload,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data ?? error?.message ?? error);
        }
    }
);

export const getTimecardRequests = createAsyncThunk<
    GetTimecardRequestsResponse,
    GetTimecardRequestsPayload,
    { rejectValue: unknown }
>(
    "timecards/getTimecardRequests",
    async (payload: GetTimecardRequestsPayload, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<GetTimecardRequestsResponse>({
                url: "/timecard/get_timecard_requests",
                data: payload,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data ?? error);
        }
    }
);
